<template>
  <div class="w-full flex flex-col flex-grow items-center p-2">
    <WorkingAndError :isWorking="isWorking" :error="responseError" @clearError="responseError = null" />
    <div v-if="showHeading" class="w-full text-left whitespace-normal truncate p-1 font-semibold sm:text-base max-w-7xl border-b text-red">Damage Report</div>
    <div v-if="equipment && typeof equipment === 'object'" class="w-full flex flex-col flex-grow max-w-7xl">
  <!-- Header — matches EditBox exactly -->
      <div class="w-full flex flex-wrap items-center border-b bg-neutral-100 dark:bg-neutral-900 z-10 flex-shrink-0">
        <div class="flex-grow flex flex-nowrap items-center p-1 min-w-64">
          <div class="flex-grow text-left px-1 whitespace-normal truncate p-1 text-sm font-semibold">
            {{ `Box ${formatBoxNumber(equipment.boxNumber, equipment.boxYear)}${equipment.projectId?.companyProjectNumber ? ' - ' + equipment.projectId.companyProjectNumber : ''}${equipment.projectId?.name ? ' - ' + equipment.projectId.name : ''}` }}
            <span v-if="equipment.returnDisposition" class="text-red p-1">{{ capitalizeString(equipment.returnDisposition) }}</span>
          </div>
          <div v-if="equipment.isKit || equipment.kitId" class="text-sky-600 font-semibold text-sm py-0.5 px-1">
            {{ equipment.isKit ? 'Kit' : 'In Kit' }}
          </div>
        </div>
      </div>
  <!-- Actions -->
      <div v-if="kitDecisionPending" class="w-full flex-shrink-0 border-b p-2">
  <!-- Kit actions -->
        <div v-if="equipment.kitItems && equipment.kitItems.length > 0" class="w-full flex items-center flex-wrap sm:justify-end">
          <transition name="tracking-in-expand" appear>
            <div class="flex-grow flex sm:justify-end text-indigo dark:text-amber font-semibold mr-3 text-sm py-1">
              What would you like to do?
            </div>
          </transition>
          <div class="w-auto flex-shrink-0 truncate flex flex-nowrap items-center">
            <div class="p-1 mr-1 min-w-32 sm:min-w-fit flex">
              <ButtonWithConfirm
                buttonClass="text-red bn-text"
                buttonText="Dispose Kit"
                popperContent="Dispose entire kit"
                :iconComponent="XIcon"
                :disabled="isWorking"
                @confirmed="submitDecision('dispose')"
              />
            </div>
            <div class="p-1 mr-1 min-w-32 sm:min-w-fit flex">
              <ButtonWithConfirm
                buttonClass="bn-text text-amber"
                buttonText="RMA Kit"
                popperContent="RMA entire kit"
                :disabled="isWorking"
                @confirmed="submitDecision('rma')"
              />
            </div>
          </div>
          <div class="w-auto flex-shrink-0 truncate flex flex-nowrap items-center">
            <div class="p-1 mr-1 min-w-32 sm:min-w-fit flex">
              <ButtonWithConfirm
                buttonClass="text-red bn-text"
                buttonText="Dispose Items"
                popperContent="Dispose damaged items only"
                :iconComponent="XIcon"
                :disabled="isWorking"
                @confirmed="submitDecision('dispose_damaged_only')"
              />
            </div>
            <div class="p-1 mr-1 min-w-32 sm:min-w-fit flex">
              <ButtonWithConfirm
                buttonClass="bn-text text-amber"
                buttonText="RMA Items"
                popperContent="RMA damaged items only"
                :disabled="isWorking"
                @confirmed="submitDecision('rma_damaged_only')"
              />
            </div>
          </div>
        </div>
  <!-- Single item actions -->
        <div v-else>
          <div v-if="equipment.damageReportComplete && !equipment.damageDecision" class="w-full flex flex-wrap items-center sm:justify-end">
          <transition name="tracking-in-expand" appear>
            <div class="flex-grow flex sm:justify-end text-indigo dark:text-amber font-semibold mr-3 text-sm py-1">
              What would you like to do?
            </div>
          </transition>
            <div class="w-auto flex-shrink-0 truncate flex flex-nowrap items-center">
              <div class="p-1 mr-1">
                <ButtonWithConfirm
                  buttonClass="text-red bn-text"
                  buttonText="Dispose"
                  popperContent="Dispose item"
                  :iconComponent="XIcon"
                  :disabled="isWorking"
                  @confirmed="submitDecision('dispose')"
                />
              </div>
              <div class="p-1 mr-1">
                <ButtonWithConfirm
                  buttonClass="bn-text"
                  buttonText="Create RMA"
                  popperContent="RMA item"
                  :disabled="isWorking"
                  @confirmed="submitDecision('rma')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
  <!-- Complete damage report -->
      <div v-if="completePending" class="w-full flex-shrink-0 border-b p-2 flex flex-wrap items-center sm:justify-end">
        <transition name="tracking-in-expand" appear>
          <div class="flex-grow flex sm:justify-end text-indigo dark:text-amber font-semibold mr-3 text-sm py-1">
            Finish the report and submit to the queue?
          </div>
        </transition>
        <div class="w-auto flex-shrink-0 truncate flex flex-nowrap items-center">
          <div class="p-1 mr-1">
            <ButtonWithConfirm
              buttonClass="text-red bn-text"
              buttonText="Cancel"
              popperContent="Undo damage — returns item to normal state"
              :iconComponent="XIcon"
              :disabled="isWorking"
              @confirmed="cancelDamage"
            />
          </div>
          <div class="p-1 mr-1">
            <ButtonWithConfirm
              buttonClass="bn-text text-green"
              buttonText="Complete"
              popperContent="Mark report complete and submit to Damage Report Queue"
              :disabled="completeDisabled"
              @confirmed="completeReport"
            />
          </div>
        </div>
      </div>
      <div v-if="isLogistics && damageStage" class="w-full flex items-center flex-nowrap border-b">
        <div class="w-auto flex-shrink-0 p-1">
          <Popper hover arrow placement="top" content="History">
            <button class="bn-icon-only" @click="showHistory = true">
                <MdManageHistory />
            </button>
          </Popper>
        </div>
        <div class="flex-grow whitespace-normal text-left p-1 ">
          <span v-if="damageStage.date" class="mr-1 text-sky">{{ format(parseISO(damageStage.date), 'EEE  MMM d, yyyy h:mm a') }}</span><span v-if="damageStage.date">: </span><span class="text-indigo dark:text-amber">{{ damageStage.label }}</span>
        </div>
      </div>
  <!-- Scrollable content -->
      <div class="w-full flex flex-col flex-grow flex-auto h-0 overflow-y-auto">
  <!-- Kit: one card per item, sorted by boxNumber -->
        <div v-if="equipment.kitItems && equipment.kitItems.length > 0" class="px-1 mb-1">
          <div
            v-for="item in allKitItems"
            :key="String(item._id)"
            class="w-full flex flex-col items-start border-b p-2 gap-y-2"
          >
          <!-- Item header -->
            <div class="w-full flex flex-row items-center flex-wrap truncate">
              <div class="w-24 min-w-24 flex-shrink-0 font-semibold text-left p-1 mr-2" :class="item.returnDisposition === 'damaged' ? 'text-red' : 'text-sky'">
                Box: {{ formatBoxNumber(item.boxNumber, item.boxYear) }}
              </div>
              <div v-if="item.manufacturer" class="w-60 min-w-60 whitespace-normal text-left truncate p-1">{{ capitalizeString(item.manufacturer) }}</div>
              <div v-if="item.partNumber" class="w-64 min-w-64 whitespace-normal text-left truncate p-1"><span class="font-semibold text-indigo mr-1">P/N:</span>{{ capitalizeString(item.partNumber) }}</div>
              <div v-if="item.description" class="flex-grow whitespace-normal text-left truncate p-1">{{ item.description }}</div>
              <span v-if="item.returnDisposition === 'damaged'" class="chip-red">Damaged</span>
            </div>
          <!-- Notes + Photos only for damaged items -->
            <div v-if="item.returnDisposition === 'damaged'" class="w-full px-2 relative">
              <div v-if="uploadingIds.includes(String(item._id))" class="absolute inset-0 bg-white bg-opacity-80 dark:bg-black dark:bg-opacity-60 z-10 flex flex-col items-center justify-center">
                <LoadingData :message="(lastUploadTotalMap[String(item._id)] || 0) === 1 ? 'Uploading photo...' : `Uploading ${lastUploadTotalMap[String(item._id)] || 0} photos...`" />
              </div>
            <!-- Notes -->
              <div class="w-full flex flex-col items-start px-1 gap-y-0.5 mb-2">
                <div class="font-semibold text-sky py-1">Damage Notes</div>
                <WorkingAndError v-if="isLogistics" :isWorking="notesUpdatingIds.includes(String(item._id))" :error="notesErrorMap[String(item._id)]" @clearError="notesErrorMap = { ...notesErrorMap, [String(item._id)]: null }" />
                <textarea
                  v-if="isLogistics && !item.damageDecision"
                  v-model.trim="notesMap[String(item._id)]"
                  v-auto-resize
                  placeholder="Notes"
                  maxlength="2000"
                  rows="2"
                  class="w-full resize-none border border-indigo-sky"
                  :disabled="notesUpdatingIds.includes(String(item._id))"
                  @blur="updateDamageNotes(String(item._id))"
                  @keyup.enter="$event?.target?.blur()"
                />
                <template v-else>
                  <div v-if="item.damageNotes" class="whitespace-pre-wrap">{{ item.damageNotes }}</div>
                  <div v-else class="text-neutral-400 italic">No notes recorded.</div>
                </template>
              </div>
            <!-- Photos -->
              <div class="w-full flex flex-col items-start px-1 gap-y-0.5">
                <div class="font-semibold text-sky py-1">Photos</div>
                <DocumentDropUpload
                  v-if="isLogistics && !item.damageDecision && (thumbnailMap[String(item._id)] || []).length < 10"
                  :ref="(el) => { if (el) uploadRefs[String(item._id)] = el; else delete uploadRefs[String(item._id)] }"
                  :title="`Add Photos for Box ${item.boxNumber}`"
                  :multiple="true"
                  :maxFiles="10 - (thumbnailMap[String(item._id)] || []).length"
                  :allowedTypes="['image/jpeg','image/png','image/gif','image/tiff','image/webp','image/heic']"
                  :allowedExtensions="['jpg','jpeg','png','gif','tiff','webp','heic']"
                  :showMetadata="false"
                  :hideFileList="true"
                  @documents-changed="onDocumentsChanged(String(item._id), $event)"
                />
                <div v-if="isLogistics && !item.damageDecision && (stagedPhotosMap[String(item._id)] || []).length > 0" class="w-full mt-2 flex flex-col">
                  <div class="flex flex-wrap gap-2">
                    <div
                      v-for="(photo, pIdx) in stagedPhotosMap[String(item._id)]"
                      :key="pIdx"
                      class="flex flex-col items-center w-20 flex-shrink-0"
                    >
                      <div class="relative group">
                        <img :src="photo.previewUrl" :alt="photo.label || 'Damage photo'" class="w-20 h-20 object-cover rounded border border-neutral-300 dark:border-neutral-600" />
                        <button type="button" class="min-h-0 border-0 absolute top-0.5 right-0.5 p-0.5 rounded bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity" @click="removePhoto(String(item._id), pIdx)" title="Remove photo">
                          <TrashIcon class="h-3 w-3" />
                        </button>
                      </div>
                      <input v-model="photo.label" type="text" placeholder="Label" maxlength="60" class="w-full mt-1" />
                    </div>
                  </div>
                  <div class="flex w-full p-1">
                    <button type="button" class="bn-solid-green mt-2 self-start animate-bounce" :disabled="uploadingIds.includes(String(item._id))" @click="doUpload(String(item._id))">
                      <div>Upload Photos</div>
                      <ArrowSmUpIcon class="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div v-if="(lastUploadTotalMap[String(item._id)] || 0) > 0 && !uploadingIds.includes(String(item._id))" class="w-full mt-1 text-left">
                  <span class="text-green">{{ lastUploadOkMap[String(item._id)] }} of {{ lastUploadTotalMap[String(item._id)] }} uploaded</span>
                </div>
                <div v-if="(uploadErrorsMap[String(item._id)] || []).length > 0" class="w-full mt-1 text-red text-left">
                  <div v-for="(err, i) in uploadErrorsMap[String(item._id)]" :key="i">{{ err }}</div>
                </div>
                <div v-if="(deleteErrorsMap[String(item._id)] || []).length > 0" class="w-full mt-1 text-red text-left">
                  <div v-for="(err, i) in deleteErrorsMap[String(item._id)]" :key="i">{{ err }}</div>
                </div>
                <div v-if="(thumbnailMap[String(item._id)] || []).length > 0" class="flex flex-wrap gap-2 mt-1">
                  <div
                    v-for="(photo, idx) in thumbnailMap[String(item._id)]"
                    :key="photo.driveFileId || idx"
                    class="flex flex-col items-center w-20 flex-shrink-0"
                  >
                    <div class="relative group">
                      <div class="relative">
                        <img
                          v-if="photo.thumbnailLink"
                          referrerpolicy="no-referrer"
                          :src="photo.thumbnailLink"
                          :alt="photo.label || 'Damage photo'"
                          class="w-20 h-20 object-cover rounded border border-neutral-300 dark:border-neutral-600 cursor-pointer"
                          :class="{ 'opacity-40': (deletingPhotoIdsMap[String(item._id)] || []).includes(photo.driveFileId) }"
                          @click="openLightbox(String(item._id), idx)"
                        />
                        <div v-else class="w-20 h-20 rounded border border-neutral-300 dark:border-neutral-600 bg-neutral-200 dark:bg-neutral-700" :class="{ 'opacity-40': (deletingPhotoIdsMap[String(item._id)] || []).includes(photo.driveFileId) }" />
                        <div v-if="(deletingPhotoIdsMap[String(item._id)] || []).includes(photo.driveFileId)" class="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
                          <div class="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      </div>
                      <button
                        v-if="isLogistics && !item.damageDecision"
                        type="button"
                        class="min-h-0 border-0 absolute top-0.5 right-0.5 p-0.5 rounded bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                        :disabled="(deletingPhotoIdsMap[String(item._id)] || []).includes(photo.driveFileId)"
                        @click="removeUploadedPhoto(String(item._id), photo)"
                        title="Remove photo"
                      >
                        <TrashIcon class="h-3 w-3" />
                      </button>
                    </div>
                    <div v-if="photo.label" class="w-full mt-1 px-1 text-center whitespace-normal line-clamp-2">{{ photo.label }}</div>
                  </div>
                </div>
                <div v-else-if="(stagedPhotosMap[String(item._id)] || []).length === 0" class="text-sm text-neutral-400 italic">No photos recorded.</div>
              </div>
            </div>
          </div>
        </div>
  <!-- Single item -->
        <div v-else class="w-full border-b">
          <div class="w-full flex flex-col items-start p-2 gap-y-2 relative">
            <div v-if="uploadingIds.includes(String(equipment._id))" class="absolute inset-0 bg-white bg-opacity-80 dark:bg-black dark:bg-opacity-60 z-10 flex flex-col items-center justify-center">
              <LoadingData :message="(lastUploadTotalMap[String(equipment._id)] || 0) === 1 ? 'Uploading photo...' : `Uploading ${lastUploadTotalMap[String(equipment._id)] || 0} photos...`" />
            </div>
          <!-- Item info -->
            <div class="w-full flex flex-row items-center flex-wrap truncate border-b">
              <div class="w-60 min-w-60 whitespace-normal text-left truncate p-1">{{ capitalizeString(equipment.manufacturer) }}</div>
              <div v-if="equipment.partNumber" class="w-64 min-w-64 whitespace-normal text-left truncate p-1"><span class="font-semibold text-indigo mr-1">P/N:</span>{{ capitalizeString(equipment.partNumber) }}</div>
              <div v-if="equipment.description" class="flex-grow whitespace-normal text-left truncate p-1">{{ equipment.description }}</div>
            </div>
          <!-- Notes -->
            <div class="w-full flex flex-col items-start px-1 gap-y-0.5">
              <div class="font-semibold text-sky">Damage Notes</div>
              <WorkingAndError v-if="isLogistics" :isWorking="notesUpdatingIds.includes(String(equipment._id))" :error="notesErrorMap[String(equipment._id)]" @clearError="notesErrorMap = { ...notesErrorMap, [String(equipment._id)]: null }" />
              <textarea
                v-if="isLogistics && !equipment.damageDecision"
                v-model.trim="notesMap[String(equipment._id)]"
                v-auto-resize
                placeholder="Notes"
                maxlength="2000"
                rows="2"
                class="w-full resize-none border border-indigo-sky"
                :disabled="notesUpdatingIds.includes(String(equipment._id))"
                @blur="updateDamageNotes(String(equipment._id))"
                @keyup.enter="$event?.target?.blur()"
              />
              <template v-else>
                <div v-if="equipment.damageNotes" class="whitespace-pre-wrap">{{ equipment.damageNotes }}</div>
                <div v-else class="text-neutral-400 italic">No notes recorded.</div>
              </template>
            </div>
          <!-- Photos -->
            <div class="w-full flex flex-col items-start px-1 gap-y-0.5">
              <div class="font-semibold text-sky">Photos</div>
              <DocumentDropUpload
                v-if="isLogistics && !equipment.damageDecision && (thumbnailMap[String(equipment._id)] || []).length < 10"
                :ref="(el) => { if (el) uploadRefs[String(equipment._id)] = el; else delete uploadRefs[String(equipment._id)] }"
                :title="`Add Photos for Box ${equipment.boxNumber}`"
                :multiple="true"
                :maxFiles="10 - (thumbnailMap[String(equipment._id)] || []).length"
                :allowedTypes="['image/jpeg','image/png','image/gif','image/tiff','image/webp','image/heic']"
                :allowedExtensions="['jpg','jpeg','png','gif','tiff','webp','heic']"
                :showMetadata="false"
                :hideFileList="true"
                @documents-changed="onDocumentsChanged(String(equipment._id), $event)"
              />
              <div v-if="isLogistics && !equipment.damageDecision && (stagedPhotosMap[String(equipment._id)] || []).length > 0" class="w-full mt-2 flex flex-col">
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="(photo, pIdx) in stagedPhotosMap[String(equipment._id)]"
                    :key="pIdx"
                    class="flex flex-col items-center w-20 flex-shrink-0"
                  >
                    <div class="relative group">
                      <img :src="photo.previewUrl" :alt="photo.label || 'Damage photo'" class="w-20 h-20 object-cover rounded border border-neutral-300 dark:border-neutral-600" />
                      <button type="button" class="min-h-0 border-0 absolute top-0.5 right-0.5 p-0.5 rounded bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity" @click="removePhoto(String(equipment._id), pIdx)" title="Remove photo">
                        <TrashIcon class="h-3 w-3" />
                      </button>
                    </div>
                    <input v-model="photo.label" type="text" placeholder="Label" maxlength="60" class="w-full mt-1" />
                  </div>
                </div>
                <div class="flex w-full p-1">
                  <button type="button" class="bn-solid-green mt-2 self-start animate-bounce" :disabled="uploadingIds.includes(String(equipment._id))" @click="doUpload(String(equipment._id))">
                    <div>Upload Photos</div>
                    <ArrowSmUpIcon class="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div v-if="(lastUploadTotalMap[String(equipment._id)] || 0) > 0 && !uploadingIds.includes(String(equipment._id))" class="w-full mt-1 text-left">
                <span class="text-green">{{ lastUploadOkMap[String(equipment._id)] }} of {{ lastUploadTotalMap[String(equipment._id)] }} uploaded</span>
              </div>
              <div v-if="(uploadErrorsMap[String(equipment._id)] || []).length > 0" class="w-full mt-1 text-red text-left">
                <div v-for="(err, i) in uploadErrorsMap[String(equipment._id)]" :key="i">{{ err }}</div>
              </div>
              <div v-if="(deleteErrorsMap[String(equipment._id)] || []).length > 0" class="w-full mt-1 text-red text-left">
                <div v-for="(err, i) in deleteErrorsMap[String(equipment._id)]" :key="i">{{ err }}</div>
              </div>
              <div v-if="(thumbnailMap[String(equipment._id)] || []).length > 0" class="flex flex-wrap gap-2 mt-1">
                <div
                  v-for="(photo, idx) in thumbnailMap[String(equipment._id)]"
                  :key="photo.driveFileId || idx"
                  class="flex flex-col items-center w-20 flex-shrink-0"
                >
                  <div class="relative group">
                    <div class="relative">
                      <img
                        v-if="photo.thumbnailLink"
                        referrerpolicy="no-referrer"
                        :src="photo.thumbnailLink"
                        :alt="photo.label || 'Damage photo'"
                        class="w-20 h-20 object-cover rounded border border-neutral-300 dark:border-neutral-600 cursor-pointer"
                        :class="{ 'opacity-40': (deletingPhotoIdsMap[String(equipment._id)] || []).includes(photo.driveFileId) }"
                        @click="openLightbox(String(equipment._id), idx)"
                      />
                      <div v-else class="w-20 h-20 rounded border border-neutral-300 dark:border-neutral-600 bg-neutral-200 dark:bg-neutral-700" :class="{ 'opacity-40': (deletingPhotoIdsMap[String(equipment._id)] || []).includes(photo.driveFileId) }" />
                      <div v-if="(deletingPhotoIdsMap[String(equipment._id)] || []).includes(photo.driveFileId)" class="absolute inset-0 flex items-center justify-center bg-black/40 rounded">
                        <div class="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </div>
                    <button
                      v-if="isLogistics && !equipment.damageDecision"
                      type="button"
                      class="min-h-0 border-0 absolute top-0.5 right-0.5 p-0.5 rounded bg-red-500/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      :disabled="(deletingPhotoIdsMap[String(equipment._id)] || []).includes(photo.driveFileId)"
                      @click="removeUploadedPhoto(String(equipment._id), photo)"
                      title="Remove photo"
                    >
                      <TrashIcon class="h-3 w-3" />
                    </button>
                  </div>
                  <div v-if="photo.label" class="w-full mt-1 px-1 text-center whitespace-normal line-clamp-2">{{ photo.label }}</div>
                </div>
              </div>
              <div v-else-if="(stagedPhotosMap[String(equipment._id)] || []).length === 0" class="text-neutral-400 italic">No photos recorded.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  <!-- Lightbox -->
    <Modal v-if="lightboxItemId !== null" :title="`Photo ${lightboxIndex + 1} of ${currentLightboxPhotos.length}`" @closeModal="closeLightbox">
      <template #content>
        <div class="w-full flex flex-col flex-1 min-h-0 p-2 gap-2">
          <div class="w-full text-center text-sm font-semibold px-2 flex-shrink-0 min-h-[1.25rem]">{{ currentLightboxPhotos[lightboxIndex]?.label || '' }}</div>
          <div class="w-full flex items-center gap-2 flex-1 min-h-0">
            <button class="bn-icon-only flex-shrink-0" :disabled="lightboxIndex === 0" @click="navigateLightbox(-1)"><ChevronLeftIcon class="w-6 h-6" /></button>
            <div class="flex-grow flex items-center justify-center min-h-0 h-full"
              @touchstart.passive="onTouchStart"
              @touchend.passive="onTouchEnd"
            >
              <div v-if="lightboxLoading" class="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
              <img v-else-if="lightboxSrc" :src="lightboxSrc" :alt="currentLightboxPhotos[lightboxIndex]?.label || 'Damage photo'" class="max-w-full max-h-full object-contain rounded" />
            </div>
            <button class="bn-icon-only flex-shrink-0" :disabled="lightboxIndex === currentLightboxPhotos.length - 1" @click="navigateLightbox(1)"><ChevronRightIcon class="w-6 h-6" /></button>
          </div>
          <div class="flex gap-1 flex-wrap justify-center flex-shrink-0 max-h-24 overflow-y-auto">
            <div
              v-for="(p, i) in currentLightboxPhotos" :key="p.driveFileId || i"
              class="w-10 h-10 rounded border-2 cursor-pointer overflow-hidden flex-shrink-0"
              :class="i === lightboxIndex ? 'border-sky-500' : 'border-transparent opacity-60 hover:opacity-100'"
              @click="openLightbox(lightboxItemId, i)"
            >
              <img v-if="p.thumbnailLink" referrerpolicy="no-referrer" :src="p.thumbnailLink" class="w-full h-full object-cover" />
              <div v-else class="w-full h-full bg-neutral-300 dark:bg-neutral-700" />
            </div>
          </div>
        </div>
      </template>
    </Modal>
  </div>
    <Modal v-if="showHistory" title="History" @closeModal="showHistory = false">
      <template v-slot:content>
        <History  :logs="equipment?.logs" />
      </template>
    </Modal>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, inject } from 'vue'
import { format, parseISO } from 'date-fns'
import { ChevronLeftIcon, ChevronRightIcon, TrashIcon, ArrowSmUpIcon,XIcon } from '@heroicons/vue/outline'
import api from '@/api'
import { capitalizeString, formatBoxNumber, broadcastChange, isOwnBroadcast } from '@/shared'
import MdManageHistory from '@/components/customIcons/MdManageHistory.vue'
import History from '@/components/ui/History.vue'
import DocumentDropUpload from '@/components/ui/DocumentDropUpload.vue'
import LoadingData from '@/components/ui/LoadingData.vue'
import ButtonWithConfirm from '@/components/ui/ButtonWithConfirm.vue'
import autoResize from '@/components/ui/autoResize'

export default {
  name: 'DamageReport',
  components: { ChevronLeftIcon, ChevronRightIcon, TrashIcon, ArrowSmUpIcon, History, MdManageHistory, DocumentDropUpload, LoadingData, ButtonWithConfirm},
  directives: { autoResize },
  emits: ['updated', 'decisionMade'],
  props: {
    equipmentId: { type: String, required: true },
    showHeading: {type:Boolean,default:true},
    // Set by a host that already reloads from this dialog's emits (EquipmentTracker). Announcements then also
    // reach this tab, tagged with the host's name so the host and this dialog skip them. Unset, this dialog
    // announces with sendChangeEvent exactly as before.
    broadcastSource: { type: String, default: null }
  },
  setup(props, { emit }) {
// Refs
    const global = inject('global')
    const { authenticated, setModalBlocked, sendChangeEvent } = global
    // One announce path: broadcastChange under the host's name when a host lends one, else sendChangeEvent.
    const announce = (type, data) => props.broadcastSource ? broadcastChange(type, data, props.broadcastSource) : sendChangeEvent(type, data)
    const isWorking = ref(false)
    const responseError = ref(null)
    const equipment = ref(null)
    const thumbnailMap = ref({})
    const lightboxItemId = ref(null)
    const lightboxIndex = ref(0)
    const lightboxSrc = ref(null)
    const lightboxLoading = ref(false)
    const showHistory = ref(false)
    const uploadRefs = ref({})
    const notesMap = ref({})
    const notesUpdatingIds = ref([])
    const notesErrorMap = ref({})
    const stagedPhotosMap = ref({})
    const uploadingIds = ref([])
    const uploadErrorsMap = ref({})
    const deleteErrorsMap = ref({})
    const deletingPhotoIdsMap = ref({})
    const lastUploadTotalMap = ref({})
    const lastUploadOkMap = ref({})

// Computed

    const isLogistics = computed(()=>{
      return authenticated.value.organization?.orgType && authenticated.value.organization.orgType.includes('primary')
      && (authenticated.value.role?.accessModel?.warehouseQueue > 0 || authenticated.value.role?.accessModel?.rackQueue > 0 || authenticated.value.role?.accessModel?.receiving > 0 )
    })

    const allKitItems = computed(() => {
      const eq = equipment.value
      if (!eq) return []
      return [eq, ...(Array.isArray(eq.kitItems) ? eq.kitItems : [])].sort((a, b) => {
        const an = parseFloat(String(a.boxNumber)) || 0
        const bn = parseFloat(String(b.boxNumber)) || 0
        return an - bn
      })
    })

    const damageStage = computed(() => {
      const eq = equipment.value
      if (!eq) return null
      if (eq.damageDecision) return { label: `Decision: ${capitalizeString(eq.returnDisposition)}${eq.damageDecisionBy ? ' by ' + eq.damageDecisionBy : ''}`, date: eq.damageDecisionMadeAt }
      if (eq.damageDecisionSentAt) return { label: 'Damage Report Sent to Client', date: eq.damageDecisionSentAt }
      return { label: 'Decision email not sent yet.', date: null }
    })

    const currentLightboxPhotos = computed(() =>
      lightboxItemId.value !== null ? (thumbnailMap.value[lightboxItemId.value] || []) : []
    )

    const hasDamagedItems = computed(() =>
      allKitItems.value.some(i => i.damageReportComplete)
    )

    const kitDecisionPending = computed(() =>
      hasDamagedItems.value &&
      !equipment.value?.damageDecision &&
      !allKitItems.value.some(i => i.damageDecision)
    )

    const completePending = computed(() => {
      const eq = equipment.value
      if (!eq || !isLogistics.value) return false
      if (eq.damageDecision) return false
      const isKit = !!(eq.kitItems && eq.kitItems.length > 0)
      if (isKit) {
        return allKitItems.value.some(i => i.returnDisposition === 'damaged' && !i.damageReportComplete)
      }
      return eq.returnDisposition === 'damaged' && !eq.damageReportComplete
    })

    const completeDisabled = computed(() =>
      isWorking.value ||
      Object.values(stagedPhotosMap.value).some(arr => Array.isArray(arr) && arr.length > 0) ||
      uploadingIds.value.length > 0 ||
      notesUpdatingIds.value.length > 0 ||
      Object.values(deletingPhotoIdsMap.value).some(arr => Array.isArray(arr) && arr.length > 0)
    )

// Watchers
    watch(completePending, (val) => {
      setModalBlocked(val)
    }, { immediate: true })

    watch(equipment, (eq) => {
      if (!eq) return
      const items = [eq, ...(Array.isArray(eq.kitItems) ? eq.kitItems : [])]
      const updates = {}
      for (const item of items) {
        const id = String(item._id)
        const saved = item.damageNotes ?? ''
        const current = notesMap.value[id]
        if (current === undefined || current === saved) updates[id] = saved
      }
      notesMap.value = { ...notesMap.value, ...updates }
    }, { immediate: true })

// API
    const fetchEquipment = async (silent = false) => {
      if (!props.equipmentId) return
      if (!silent) isWorking.value = true
      responseError.value = null
      try {
        const res = await api.get(`logistics/equipment/${props.equipmentId}/damageReport`)
        equipment.value = res.data?.data ?? null
        await loadAllThumbnails(silent)
      } catch (err) {
        if (!silent) responseError.value = err.response?.data?.error || err.message
      } finally {
        if (!silent) isWorking.value = false
      }
    }

    const loadThumbnailsForItem = async (item) => {
      const id = String(item._id)
      const photos = Array.isArray(item.damagePhotos) ? item.damagePhotos : []
      if (photos.length === 0) {
        thumbnailMap.value = { ...thumbnailMap.value, [id]: [] }
        return
      }
      thumbnailMap.value = { ...thumbnailMap.value, [id]: photos }
      try {
        const res = await api.get(`equipment/damagePhotos/${id}/thumbnails`)
        const fetched = res.data?.data?.damagePhotos || []
        if (fetched.length) thumbnailMap.value = { ...thumbnailMap.value, [id]: fetched }
      } catch {
        // keep raw photos as fallback
      }
    }

    const loadAllThumbnails = async (skipUnchanged = false) => {
      const eq = equipment.value
      if (!eq) return
      const items = [eq, ...(Array.isArray(eq.kitItems) ? eq.kitItems : [])]
      const toLoad = items.filter(i => {
        if (!i.damageReportComplete) return false
        if (!skipUnchanged) return true
        const id = String(i._id)
        const incoming = (Array.isArray(i.damagePhotos) ? i.damagePhotos : []).map(p => p.driveFileId).sort().join(',')
        const existing = (thumbnailMap.value[id] || []).map(p => p.driveFileId).sort().join(',')
        return incoming !== existing
      })
      await Promise.all(toLoad.map(loadThumbnailsForItem))
    }

    const updateDamageNotes = async (itemId) => {
      if (notesUpdatingIds.value.includes(itemId)) return
      const eq = equipment.value
      const item = eq?._id && String(eq._id) === itemId ? eq
        : (eq?.kitItems || []).find(i => String(i._id) === itemId)
      if (!item) return
      const notes = (notesMap.value[itemId] ?? '').trim()
      if (notes === (item.damageNotes ?? '').trim()) return
      notesErrorMap.value = { ...notesErrorMap.value, [itemId]: null }
      notesUpdatingIds.value = [...notesUpdatingIds.value, itemId]
      setModalBlocked(true)
      try {
        await api.put(`equipment/damageNotes/${itemId}`, { damageNotes: notes })
        await fetchEquipment(true)
        emit('updated')
      } catch (err) {
        notesErrorMap.value = { ...notesErrorMap.value, [itemId]: err.response?.data?.error || err.message }
      } finally {
        notesUpdatingIds.value = notesUpdatingIds.value.filter(id => id !== itemId)
        setModalBlocked(completePending.value)
      }
    }

    const onDocumentsChanged = (itemId, { documents }) => {
      const existing = stagedPhotosMap.value[itemId] || []
      const result = documents.map(doc => {
        const match = existing.find(e => e.file.name === doc.name && e.file.size === doc.size)
        return { file: doc.file, label: match?.label ?? '', previewUrl: match?.previewUrl ?? URL.createObjectURL(doc.file) }
      })
      existing.forEach(e => {
        if (!result.find(r => r.file.name === e.file.name && r.file.size === e.file.size)) URL.revokeObjectURL(e.previewUrl)
      })
      stagedPhotosMap.value = { ...stagedPhotosMap.value, [itemId]: result }
    }

    const removePhoto = (itemId, index) => {
      uploadRefs.value[itemId]?.removeFile?.(index)
    }

    const doUpload = async (itemId) => {
      const staged = stagedPhotosMap.value[itemId] || []
      if (staged.length === 0 || uploadingIds.value.includes(itemId)) return
      const eq = equipment.value
      const item = eq?._id && String(eq._id) === itemId ? eq
        : (eq?.kitItems || []).find(i => String(i._id) === itemId)
      if (!item) return
      const currentCount = (item.damagePhotos || []).length
      const remaining = 10 - currentCount
      if (remaining <= 0) { uploadErrorsMap.value = { ...uploadErrorsMap.value, [itemId]: ['Maximum 10 photos allowed'] }; return }
      let toUpload = [...staged]
      if (toUpload.length > remaining) {
        toUpload = toUpload.slice(0, remaining)
        stagedPhotosMap.value = { ...stagedPhotosMap.value, [itemId]: toUpload }
        uploadErrorsMap.value = { ...uploadErrorsMap.value, [itemId]: [`Only ${remaining} photo${remaining !== 1 ? 's' : ''} uploaded — maximum of 10 reached`] }
      } else {
        uploadErrorsMap.value = { ...uploadErrorsMap.value, [itemId]: [] }
      }
      uploadingIds.value = [...uploadingIds.value, itemId]
      lastUploadTotalMap.value = { ...lastUploadTotalMap.value, [itemId]: toUpload.length }
      lastUploadOkMap.value = { ...lastUploadOkMap.value, [itemId]: 0 }
      setModalBlocked(true)
      const form = new FormData()
      const labels = []
      for (const p of toUpload) { form.append('dlsFile', p.file); labels.push(p.label) }
      form.append('labels', JSON.stringify(labels))
      try {
        // noRetry: the handler appends to damagePhotos, so a silent re-send after a lost response
        // would add the same photos to the box a second time.
        const res = await api.post(`equipment/damagePhotos/${itemId}`, form, { headers: { 'Content-Type': 'multipart/form-data' }, noRetry: true })
        // A photo the server could not read is skipped, one error each (naming the file), while the rest are stored
        const skipped = Array.isArray(res.data?.errors) ? res.data.errors : []
        lastUploadOkMap.value = { ...lastUploadOkMap.value, [itemId]: toUpload.length - skipped.length }
        if (skipped.length) uploadErrorsMap.value = { ...uploadErrorsMap.value, [itemId]: [...(uploadErrorsMap.value[itemId] || []), ...skipped] }
        toUpload.forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl) })
        stagedPhotosMap.value = { ...stagedPhotosMap.value, [itemId]: [] }
        uploadRefs.value[itemId]?.clearAll?.()
        await fetchEquipment(true)
        emit('updated')
      } catch (err) {
        uploadErrorsMap.value = { ...uploadErrorsMap.value, [itemId]: [err.response?.data?.error || err.message || 'Upload failed'] }
      } finally {
        uploadingIds.value = uploadingIds.value.filter(id => id !== itemId)
        setModalBlocked(completePending.value)
      }
    }

    const removeUploadedPhoto = async (itemId, photo) => {
      if (!photo?.driveFileId) return
      const deleting = deletingPhotoIdsMap.value[itemId] || []
      if (deleting.includes(photo.driveFileId)) return
      deletingPhotoIdsMap.value = { ...deletingPhotoIdsMap.value, [itemId]: [...deleting, photo.driveFileId] }
      setModalBlocked(true)
      try {
        await api.delete(`equipment/damagePhotos/${itemId}/${encodeURIComponent(photo.driveFileId)}`)
        deleteErrorsMap.value = { ...deleteErrorsMap.value, [itemId]: [] }
        thumbnailMap.value = { ...thumbnailMap.value, [itemId]: (thumbnailMap.value[itemId] || []).filter(p => p.driveFileId !== photo.driveFileId) }
        await fetchEquipment(true)
        emit('updated')
      } catch (err) {
        const errs = deleteErrorsMap.value[itemId] || []
        deleteErrorsMap.value = { ...deleteErrorsMap.value, [itemId]: [...errs, err.response?.data?.error || err.message || 'Delete failed'] }
      } finally {
        deletingPhotoIdsMap.value = { ...deletingPhotoIdsMap.value, [itemId]: (deletingPhotoIdsMap.value[itemId] || []).filter(id => id !== photo.driveFileId) }
        setModalBlocked(completePending.value)
      }
    }

    const completeReport = async () => {
      if (isWorking.value || completeDisabled.value) return
      const eq = equipment.value
      if (!eq) return
      const isKit = !!(eq.kitItems && eq.kitItems.length > 0)
      const url = isKit
        ? `equipment/damageReportComplete/kit/${eq.kitId || eq._id}`
        : `equipment/damageReportComplete/${props.equipmentId}`
      isWorking.value = true
      responseError.value = null
      setModalBlocked(true)
      try {
        await api.put(url)
        announce('equipment', { ...(isKit ? {} : { equipmentId: eq._id }), projectId: eq.projectId?._id, isDamageReport: true })
        await fetchEquipment(true)
        emit('updated')
      } catch (err) {
        responseError.value = err.response?.data?.error || err.message
      } finally {
        isWorking.value = false
        setModalBlocked(completePending.value)
      }
    }

    const cancelDamage = async () => {
      if (isWorking.value) return
      const eq = equipment.value
      if (!eq) return
      const isKit = !!(eq.kitItems && eq.kitItems.length > 0)
      isWorking.value = true
      responseError.value = null
      setModalBlocked(true)
      try {
        if (isKit) {
          const targets = (eq.kitItems || []).filter(i => i.returnDisposition === 'damaged' && !i.damageReportComplete)
          if (eq.returnDisposition === 'damaged' && !eq.damageReportComplete) targets.push(eq)
          await Promise.all(targets.map(i => api.put(`logistics/equipment/removeDamage/${i._id}`)))
          announce('equipment', { projectId: eq.projectId?._id, isDamageReport: true })
        } else {
          await api.put(`logistics/equipment/removeDamage/${props.equipmentId}`)
          announce('equipment', { equipmentId: eq._id, projectId: eq.projectId?._id, isDamageReport: true })
        }
        emit('updated')
        emit('decisionMade')
      } catch (err) {
        responseError.value = err.response?.data?.error || err.message
      } finally {
        isWorking.value = false
        setModalBlocked(completePending.value)
      }
    }

    const submitDecision = async (decision) => {
      if (isWorking.value) return
      isWorking.value = true
      responseError.value = null
      setModalBlocked(true)
      try {
        const res = await api.post(`logistics/equipment/damage/${props.equipmentId}/staffDecision`, { decision })
        const updated = res.data?.data ?? null
        equipment.value = updated
        const isKit = !!(updated?.isKit || updated?.kitId)
        announce('equipment', { ...(isKit ? {} : { equipmentId: updated?._id }), projectId: updated?.projectId?._id, isDamageReport: true })
        if (decision === 'dispose' || decision === 'dispose_damaged_only') announce('warehouseRequest', { projectId: updated?.projectId?._id, type: 'dispose' })
        if (decision === 'rma' || decision === 'rma_damaged_only') announce('rma', { equipmentId: updated?._id })
        await fetchEquipment(true)
        emit('updated')
        emit('decisionMade')
      } catch (err) {
        responseError.value = err.response?.data?.error || err.message
      } finally {
        isWorking.value = false
        setModalBlocked(completePending.value)
      }
    }

// Helpers
    const openLightbox = async (itemId, index) => {
      const photos = thumbnailMap.value[itemId] || []
      const photo = photos[index]
      if (!photo) return
      if (lightboxSrc.value) URL.revokeObjectURL(lightboxSrc.value)
      lightboxItemId.value = itemId
      lightboxIndex.value = index
      lightboxSrc.value = null
      lightboxLoading.value = true
      try {
        const res = await api.get(`equipment/damagePhotos/${itemId}/file/${encodeURIComponent(photo.driveFileId)}`, { responseType: 'blob' })
        lightboxSrc.value = URL.createObjectURL(res.data)
      } catch {
        lightboxSrc.value = photo.thumbnailLink || null
      } finally {
        lightboxLoading.value = false
      }
    }

    const closeLightbox = () => {
      if (lightboxSrc.value) URL.revokeObjectURL(lightboxSrc.value)
      lightboxItemId.value = null
      lightboxIndex.value = 0
      lightboxSrc.value = null
    }

    const navigateLightbox = (dir) => {
      const next = lightboxIndex.value + dir
      const photos = currentLightboxPhotos.value
      if (next >= 0 && next < photos.length) openLightbox(lightboxItemId.value, next)
    }

    let touchStartX = null
    const onTouchStart = (e) => { touchStartX = e.touches[0]?.clientX ?? null }
    const onTouchEnd = (e) => {
      if (touchStartX === null) return
      const dx = (e.changedTouches[0]?.clientX ?? touchStartX) - touchStartX
      if (Math.abs(dx) > 40) navigateLightbox(dx < 0 ? 1 : -1)
      touchStartX = null
    }

// Handlers
    const onSocketChange = (e) => {
      if (isOwnBroadcast(e, props.broadcastSource)) return
      if (e.detail?.type !== 'equipment') return
      const changedId = e.detail?.data?.equipmentId
      const isDamageReport = e.detail?.data?.isDamageReport
      const eq = equipment.value
      if (!eq) return
      if (changedId) {
        const ids = [String(eq._id), ...(Array.isArray(eq.kitItems) ? eq.kitItems.map(i => String(i._id)) : [])]
        if (ids.includes(String(changedId))) fetchEquipment(true)
      } else if (isDamageReport && (eq.isKit || eq.kitId)) {
        // Kit-level damage decision — no equipmentId in event; refresh if current item is part of a kit
        fetchEquipment(true)
      }
    }

// Lifecycle
    onMounted(() => {
      fetchEquipment()
      window.addEventListener('data_change_from_socket', onSocketChange)
    })
    onBeforeUnmount(() => {
      window.removeEventListener('data_change_from_socket', onSocketChange)
      if (lightboxSrc.value) URL.revokeObjectURL(lightboxSrc.value)
      setModalBlocked(false)
    })

    return {
      isWorking, responseError, equipment, thumbnailMap, allKitItems,
      lightboxItemId, lightboxIndex, lightboxSrc, lightboxLoading, currentLightboxPhotos,
      openLightbox, closeLightbox, navigateLightbox, onTouchStart, onTouchEnd,
      capitalizeString, formatBoxNumber, format, parseISO, isLogistics, damageStage, showHistory,
      uploadRefs, notesMap, notesUpdatingIds, notesErrorMap,
      stagedPhotosMap, uploadingIds, uploadErrorsMap, deleteErrorsMap,
      deletingPhotoIdsMap, lastUploadTotalMap, lastUploadOkMap,
      updateDamageNotes, onDocumentsChanged, removePhoto, doUpload, removeUploadedPhoto,
      hasDamagedItems, kitDecisionPending, completePending, completeDisabled, completeReport, cancelDamage, XIcon, submitDecision
    }
  }
}
</script>
